import { downloadBlobAsFile, getDefaultRange } from '../../utils/utils';
import { format } from 'date-fns';
import { useFilters } from '../../contexts/filters/FiltersContext';
import { useAccount } from '../../contexts/account/AccountContexts';
import { exportProjectSources } from '../../services/projects/ProjectService';
import {
  ExportProjectBrandScoreRequest,
  exportProjectBrandScore,
  exportProjectStats,
} from '../../api/services/project/stats/ProjectDailyStatsService';
import { MonitorPeriod } from '../../types/monitor/MonitorPeriod';

const useExportData = (projectIdNumber: number) => {
  const { range, selectedChatType, domain } = useFilters();
  const { projectResponse } = useAccount();

  const exportStats = async () => {
    try {
      const defaultRange = getDefaultRange();
      const startDate = range && range[0] ? range[0] : defaultRange[0];
      const endDate = range && range[1] ? range[1] : defaultRange[1];

      const blob = await exportProjectStats(
        projectIdNumber,
        selectedChatType || 'ALL',
        startDate,
        endDate
      );

      const fileName = prepareFileName('stats', startDate, endDate);
      downloadBlobAsFile(blob, fileName);
    } catch (err) {
      console.error(err);
    }
  };

  const exportSources = async () => {
    try {
      const defaultRange = getDefaultRange();
      const startDate = range && range[0] ? range[0] : defaultRange[0];
      const endDate = range && range[1] ? range[1] : defaultRange[1];

      const blob = await exportProjectSources(
        projectIdNumber,
        selectedChatType || 'ALL',
        domain || 'ALL',
        startDate,
        endDate
      );

      const fileName = prepareFileName('sources', startDate, endDate);
      downloadBlobAsFile(blob, fileName);
    } catch (err) {
      console.error(err);
    }
  };

  const exportBrandScore = async (monitorPeriod: MonitorPeriod | null) => {
    try {
      if (!monitorPeriod) {
        return;
      }

      const defaultRange = getDefaultRange();
      const startDate = format(range && range[0] ? range[0] : defaultRange[0], 'yyyy-MM-dd');
      const endDate = format(range && range[1] ? range[1] : defaultRange[1], 'yyyy-MM-dd');

      const exportProjectProjectBrandScoreRequest: ExportProjectBrandScoreRequest = {
        projectId: projectIdNumber,
        period: monitorPeriod,
        chatType: selectedChatType || 'ALL',
        startDate,
        endDate,
      };

      const blob = await exportProjectBrandScore(exportProjectProjectBrandScoreRequest);
      const fileName = `brand-score-${selectedChatType || 'ALL'}-${startDate}-${endDate}.csv`;
      downloadBlobAsFile(blob, fileName);
    } catch (err) {
      console.error(err);
    }
  };

  const prepareFileName = (prefix: string, startDate: Date, endDate: Date): string => {
    const project = projectResponse?.find((project) => project.id === projectIdNumber);
    if (!project) {
      throw Error('Wrong project id');
    }

    const projectLabel = project.projectLabel;
    return `${prefix}-${projectLabel}-${format(startDate, 'yyyyMMdd')}-${format(
      endDate,
      'yyyyMMdd'
    )}.csv`;
  };

  return { exportStats, exportSources, exportBrandScore };
};

export default useExportData;
